/**
 * SOUND-643 — status labels and tones for the CRM record pages.
 *
 * Kept per-entity rather than in one lookup, for the reason
 * components/Tickets/TicketStatusPill states about itself: ticket, MID,
 * MID-application and registration vocabularies SHARE key names — `new`,
 * `approved`, `declined`, `withdrawn` — while meaning different things. One
 * merged table would silently collide.
 *
 * Semantics are consistent across all of them, which is what makes the pages
 * feel like one product: green is a good terminal state, red a refusal, amber
 * waiting on someone, blue in flight, brand freshly created.
 */

const TONE = {
  brand: { badge: "badge-muted", dot: "bg-brand" },
  info: { badge: "badge-info", dot: "bg-info" },
  warning: { badge: "badge-warning", dot: "bg-warning" },
  success: { badge: "badge-success", dot: "bg-success" },
  destructive: { badge: "badge-destructive", dot: "bg-destructive" },
  muted: { badge: "badge-muted", dot: "bg-muted-foreground" },
};

// ---- MID applications (config/statusMachines MID_TRANSITIONS) ----
//
// Key order is the pipeline order, because the status filter on
// /mid-applications and the company MID Applications tab both render
// Object.entries(MID_APP_STATUS) directly — so this object IS the dropdown.
//
// out_for_signature / submitted / underwriting added 2026-08-19 alongside the
// API change of the same name. Tones follow the convention at the top of this
// file: amber = waiting on someone (the merchant has to sign), blue = in
// flight (it is with the processor).
export const MID_APP_STATUS = {
  new: { label: "New", tone: "brand" },
  in_progress: { label: "In Progress", tone: "info" },
  out_for_signature: { label: "Out for Signature", tone: "warning" },
  submitted: { label: "Submitted", tone: "info" },
  underwriting: { label: "Underwriting", tone: "info" },
  approved: { label: "Approved", tone: "success" },
  declined: { label: "Declined", tone: "destructive" },
  withdrawn: { label: "Withdrawn", tone: "muted" },
};

/**
 * Allowed MID-application transitions — mirrors sound-api
 * config/statusMachines.mjs MID_TRANSITIONS.
 *
 * Lives here, next to the labels, because it used to live a THIRD time inside
 * pages/mid-applications/MidApplication.jsx. When the vocabulary gained
 * out_for_signature / submitted / underwriting on 2026-08-19, that private copy
 * was missed and the detail page's status dropdown silently kept offering the
 * old six — the new statuses were unreachable from the UI even though the API
 * accepted them. One copy per repo is the most this can safely have.
 */
export const MID_APP_TRANSITIONS = {
  new: ["in_progress", "out_for_signature", "withdrawn"],
  in_progress: [
    "out_for_signature",
    "submitted",
    "underwriting",
    "approved",
    "declined",
    "withdrawn",
  ],
  out_for_signature: ["submitted", "in_progress", "withdrawn"],
  submitted: ["underwriting", "approved", "declined", "withdrawn"],
  underwriting: ["approved", "declined", "withdrawn"],
  declined: ["in_progress"],
  approved: [],
  withdrawn: [],
};

/** Label lookup derived from MID_APP_STATUS, so the two cannot disagree. */
export const MID_APP_LABELS = Object.fromEntries(
  Object.entries(MID_APP_STATUS).map(([k, v]) => [k, v.label])
);

/**
 * MID-application statuses that mean "moving, not finished".
 *
 * `new` is excluded (nothing has happened to it yet) and so are the three
 * outcomes. Exported because a KPI keyed on the single `in_progress` status
 * undercounts: after the 2026-08-19 vocabulary change an application in flight
 * is just as likely to be out_for_signature, submitted or underwriting, and a
 * card reading "In Progress 99" while 208 deals were moving is the same
 * undercount SOUND-295 fixed on the other list pages.
 */
export const MID_APP_IN_FLIGHT = [
  "in_progress",
  "out_for_signature",
  "submitted",
  "underwriting",
];

/** Sum a statusCounts map over the in-flight statuses. */
export function midAppInFlightCount(statusCounts) {
  if (!statusCounts) return 0;
  return MID_APP_IN_FLIGHT.reduce((n, s) => n + (statusCounts[s] ?? 0), 0);
}

// ---- MIDs in the portfolio (MID_MANAGER_STATUSES) ----
// SOUND-709 — the credentials list. `kind` is what a reader is actually
// scanning for on this surface: a portal login and an API credential are
// different things to work with, and until now the page could show only one of
// them. Tone carries no judgement — none of these states is a problem.
export const CREDENTIAL_KIND = {
  api: { label: "API", tone: "info" },
  portal: { label: "Portal", tone: "brand" },
  hybrid: { label: "Hybrid", tone: "info" },
};

export const MID_STATUS = {
  active: { label: "Active", tone: "success" },
  suspended: { label: "Suspended", tone: "warning" },
  closed: { label: "Closed", tone: "muted" },
};

function resolve(map, status) {
  return map[status] || { label: status || "—", tone: "muted" };
}

export function statusLabelFrom(map) {
  return (status) => resolve(map, status).label;
}

/** Dot class for the timeline rail. */
export function statusDotFrom(map) {
  return (status) => TONE[resolve(map, status).tone].dot;
}

/** Badge + dot classes for the pill. */
export function statusToneFrom(map) {
  return (status) => {
    const { label, tone } = resolve(map, status);
    return { label, ...TONE[tone] };
  };
}

// ---- The PENDED STATE (SOUND-688) ----
//
// `pended` used to be a status. It was never a stage: it is a STATE that
// overlays whatever stage an application is at. Prod proved it — all 21
// applications at currentStatus=pended carried ZERO pends, while the 27 that
// actually had open pends sat in five other statuses. The two sets did not
// intersect at all, so the stored status was answering a question nobody asked.
//
// CPO 2026-08-24: "It's a state. If there is a pend, it is pended as a state,
// regardless of what stage it's in."
//
// It is DERIVED, never stored, so it cannot drift the way the status did. The
// API serves `unresolvedPendCount` on both the list and the detail response
// (and `isPended` on detail); the local pend-array fallback covers a response
// cached from before that shipped. It is not a second definition of "open" —
// a pend is open when `resolvedAt` is absent — and it should be deleted once
// nothing can serve the old shape.

/**
 * Open pends for an application. Server-derived, always.
 *
 * The local `pends` fallback is GONE (SOUND-689, 2026-08-24): the embedded
 * `midApplications.pends[]` array was dropped from all 1,008 documents once the
 * collection had been truth for a full deploy cycle, so there is no old shape
 * left to serve. Keeping the fallback would be worse than removing it — it reads
 * as a safety net that cannot catch anything, and would quietly report 0 if
 * `unresolvedPendCount` ever went missing rather than surfacing the gap.
 */
export const openPendCount = (app) => app?.unresolvedPendCount ?? 0;

// There is deliberately no `isPended` helper. It would have exactly zero
// callers: the pended STATE is shown by the Pends column on the list and by the
// Pends panel on the detail page, both of which want the COUNT, not a boolean.
// SOUND-690 (the Focus view) is the first surface that needs to group on the
// state — it can add the predicate then, with a caller.
